/**
 * AiClient — superficie do hook.ai.
 *
 * API publica:
 *   - hook.ai.run(taskKey, input, schema)
 *   - hook.ai.budget()
 *
 * App declara o schema (geralmente Zod) localmente e passa pra run().
 * SDK valida response runtime via schema.parse() — se falhar, lanca
 * AiError generico. Sem codegen no MVP — task nova so precisa de
 * INSERT no DB + declaracao de schema no app.
 */
import type { AiBudget, SchemaValidator } from './types';
import { mapAiError, AiError } from './errors';

export interface AiClientOpts {
  apiHost: string;
  appId: string;
}

export class AiClient {
  constructor(private readonly opts: AiClientOpts) {}

  /**
   * Executa uma task de IA registrada em ai_tasks.
   *
   * @param taskKey — string ID da task (ex: 'estimate-calories-image')
   * @param input — payload validado server-side contra ai_tasks.input_schema
   * @param schema — validador local (Zod schema, Valibot etc.) pro output
   * @returns output validado contra schema
   *
   * @throws AiTaskNotFoundError, AiInputError, AiBudgetExceededError,
   *         AiRateLimitError, AiProviderError, AiOutputError, AiTimeoutError,
   *         AiUnauthorizedError, AiError (fallback)
   */
  async run<T>(
    taskKey: string,
    input: object,
    schema: SchemaValidator<T>,
  ): Promise<T> {
    const url = `${this.opts.apiHost}/api/app/${this.opts.appId}/ai/run`;
    let res: Response;
    try {
      res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ task_key: taskKey, input }),
      });
    } catch (err) {
      throw new AiError(
        `AI network error: ${err instanceof Error ? err.message : String(err)}`,
        'ai.network',
        0,
      );
    }

    if (!res.ok) {
      throw await mapAiError(res);
    }

    const json: unknown = await res.json();
    try {
      return schema.parse(json);
    } catch (err) {
      // Schema do app rejeita output do server (apesar do server ter validado
      // com Ajv). Pode ser drift entre schema TS local e ai_tasks.output_schema
      // do server, ou bug. App deve relogar erro pro debug.
      throw new AiError(
        `AI response failed local schema: ${err instanceof Error ? err.message : String(err)}`,
        'ai.client_schema_mismatch',
        500,
      );
    }
  }

  /**
   * Le o budget atual do app (used/limit/reset_at).
   *
   * @throws AiTaskNotFoundError (404 se app nunca chamou IA — provisao via
   *         seed inicial no `bun run new-app`).
   */
  async budget(): Promise<AiBudget> {
    const url = `${this.opts.apiHost}/api/app/${this.opts.appId}/ai/budget`;
    let res: Response;
    try {
      res = await fetch(url, {
        method: 'GET',
        credentials: 'include',
      });
    } catch (err) {
      throw new AiError(
        `AI network error: ${err instanceof Error ? err.message : String(err)}`,
        'ai.network',
        0,
      );
    }

    if (!res.ok) {
      throw await mapAiError(res);
    }

    return (await res.json()) as AiBudget;
  }
}

export function createAiClient(opts: AiClientOpts): AiClient {
  return new AiClient(opts);
}
